import { useCallback, useEffect, useRef, useState } from "react";
import OneSignal from "react-onesignal";
import { initOneSignal } from "@/lib/onesignal";

export type PushState =
  | "checking"
  | "blocked"
  | "promptable"
  | "enabled"
  | "loading";

type UsePushNotificationsOptions = {
  enabled?: boolean;
  userAddress?: string;
};

export function usePushNotifications(options: UsePushNotificationsOptions = {}) {
  const { enabled = true, userAddress } = options;
  const [state, setState] = useState<PushState>("checking");
  const cleanup = useRef<() => void>(() => {});
  const lastLoginRef = useRef<string | null>(null);

  console.log("push:", { state });

  const updateState = useCallback(() => {
    try {
      const supported = OneSignal.Notifications.isPushSupported();
      if (!supported) {
        setState("blocked");
        return;
      }
      // Prefer native Notification API to detect explicit denial
      const denied =
        typeof window !== "undefined" &&
        typeof Notification !== "undefined" &&
        Notification.permission === "denied";

      if (denied) {
        setState("blocked");
        return;
      }

      // Fall back to OneSignal permission boolean for granted/promptable
      const permission = OneSignal.Notifications.permission; // boolean (true when granted)
      const optedIn = OneSignal.User.PushSubscription.optedIn === true;
      if (permission && optedIn) {
        setState("enabled");
      } else {
        setState("promptable");
      }
    } catch {
      // If SDK not ready or throws, remain in checking until init completes
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    if (!enabled || !userAddress) {
      setState("checking");
      return () => {
        mounted = false;
        cleanup.current();
      };
    }
    (async () => {
      try {
        await initOneSignal();
        if (userAddress && lastLoginRef.current !== userAddress) {
          await OneSignal.login(userAddress);
          lastLoginRef.current = userAddress;
        }
        if (!mounted) return;
        updateState();

        const onPerm = () => updateState();
        const onSubChange = () => updateState();

        OneSignal.Notifications.addEventListener("permissionChange", onPerm);
        OneSignal.User.PushSubscription.addEventListener("change", onSubChange);

        cleanup.current = () => {
          OneSignal.Notifications.removeEventListener(
            "permissionChange",
            onPerm,
          );
          OneSignal.User.PushSubscription.removeEventListener(
            "change",
            onSubChange,
          );
        };
      } catch {
        if (!mounted) return;
        setState("blocked");
      }
    })();

    return () => {
      mounted = false;
      cleanup.current();
    };
  }, [enabled, updateState, userAddress]);

  const enable = useCallback(async () => {
    if (!enabled || !userAddress) return;
    setState("loading");
    await initOneSignal();
    if (lastLoginRef.current !== userAddress) {
      await OneSignal.login(userAddress);
      lastLoginRef.current = userAddress;
    }
    await OneSignal.Notifications.requestPermission();
    await OneSignal.User.PushSubscription.optIn();
    updateState();
  }, [enabled, updateState, userAddress]);

  const disable = useCallback(async () => {
    if (!enabled) return;
    setState("loading");
    await initOneSignal();
    await OneSignal.User.PushSubscription.optOut();
    updateState();
  }, [enabled, updateState]);

  return { state, enable, disable } as const;
}
